import * as reportError from 'airship/modules/error-reporting'
import * as debugLog from '../debug-log'
import * as backend from './backend'
import {AmplitudeClient, GenericClient} from './clients'
import {
  Action,
  AnalyticsProvider,
  Context,
  EventProps,
  EventBackendProps,
  EventPropsSchema,
  LegacyEventProps,
  InjectedWindow,
  UserBackendProps,
  UserId
} from './types'

const injectedWindow = window as InjectedWindow

// https://help.amplitude.com/hc/en-us/articles/115002889587-JavaScript-SDK-Reference
const Amplitude = new AmplitudeClient(injectedWindow.amplitude)
// https://docs.heap.io/reference
const Heap = new GenericClient('Heap', injectedWindow.heap)
// Lol
const Kissmetrics = new GenericClient('Kissmetrics', injectedWindow.analytics)
// https://developer.mixpanel.com/docs/javascript
const Mixpanel = new GenericClient('Mixpanel', injectedWindow.mixpanel)

const LEGACY_PROVIDERS = [Kissmetrics]
const NEW_PROVIDERS = [Amplitude, Heap, Mixpanel]
const PROVIDERS = NEW_PROVIDERS.concat(LEGACY_PROVIDERS)

/**
 * ProviderPromise is a generic way to track an attempt to do something in an
 * analytics platform. It's purely so we can log the result in a consistent way
 * afterward.
 */
interface ProviderPromise {
  provider: AnalyticsProvider
  promise: Promise<boolean>
}

/**
 * Provides a generic API for sending to an arbitary number of analytics
 * platforms. Functions are heavily guarded to prevent errors from interrupting
 * customer logic.
 */
class Analytics {
  /**
   * tracks is for _structured_ tracking. It's the new method for reporting to
   * new platforms, and will help force enforce our event taxonomy.
   *
   * This will report events with names like "Composer: Click Message Type".
   */
  public async tracks(
    context: Context,
    action: Action,
    description: string,
    data?: EventPropsSchema
  ): Promise<void> {
    try {
      await this.sendTrackEvent(`${context}: ${action} ${description}`, data)
    } catch (err) {
      reportError(err, __filename)
    }
  }

  /**
   * DEPRECATED. track is the legacy method to report to Kissmetrics. Events
   * will be sent to other platforms but will tagged with a legacy prefix.
   */
  public async track(eventName: string, data?: LegacyEventProps) {
    try {
      // Send to Kissmetrics with the provided name.
      await this.sendTrackEvent(eventName, data, LEGACY_PROVIDERS)
      // Send to other platorms with the legacy prefix.
      await this.sendTrackEvent(`[Legacy] ${eventName}`, data, NEW_PROVIDERS)
    } catch (err) {
      reportError(err, __filename)
    }
  }

  /**
   * Set user-level properties. We set these behind the scenes based on
   * properties that the backend provides. We don't currently have a use case
   * for setting these from the frontend. So for now, this is a private method.
   */
  private async setUserProps(
    props: UserBackendProps,
    providers: AnalyticsProvider[] = PROVIDERS
  ) {
    if (props.id) {
      this.setUserId(props.id)
      delete props.id
    }

    const promises: ProviderPromise[] = providers.map(provider => {
      return {
        provider,
        promise: provider.setUserProps(props)
      }
    })
    const sentTo = await this.getSuccessProviderNames(promises)
    debugLog.log(
      'Set user properties in %s:',
      sentTo.length ? sentTo.join(', ') : 'no platforms',
      props
    )
  }

  /**
   * Set user ID. See docstring for setUserProps.
   */
  private async setUserId(
    id: UserId,
    providers: AnalyticsProvider[] = PROVIDERS
  ) {
    const promises: ProviderPromise[] = providers.map(provider => {
      return {
        provider,
        promise: provider.setUserId(id)
      }
    })
    const sentTo = await this.getSuccessProviderNames(promises)
    debugLog.log(
      'Set user ID to %s in %s:',
      id,
      sentTo.length ? sentTo.join(', ') : 'no platforms'
    )
  }

  /**
   * Send a tracking event via the given providers.
   */
  private async sendTrackEvent(
    eventName: string,
    props?: EventProps,
    providers: AnalyticsProvider[] = PROVIDERS
  ): Promise<void> {
    if (!props) {
      props = {}
    }

    let combinedProps: EventProps & EventBackendProps = props

    try {
      const backendProps = await backend.getEventProperties()
      if (backendProps) {
        if (backendProps.user) {
          this.setUserProps(backendProps.user).catch(err => {
            reportError(err, __filename)
          })
        }
        if (backendProps.event) {
          combinedProps = {...combinedProps, ...backendProps.event}
        }
      }
    } catch (err) {
      reportError(err, __filename)
    }

    const promises: ProviderPromise[] = providers.map(provider => {
      return {
        provider,
        promise: provider.track(eventName, combinedProps)
      }
    })
    const sentTo = await this.getSuccessProviderNames(promises)
    debugLog.log(
      'Sent analytics event "%s" to %s',
      eventName,
      sentTo.length ? sentTo.join(', ') : 'no platforms',
      combinedProps
    )
  }

  /**
   * Return an array of names of the providers we successfully sent to.
   */
  private async getSuccessProviderNames(
    promises: ProviderPromise[]
  ): Promise<string[]> {
    const result: string[] = []
    for (const p of promises) {
      try {
        const success = await p.promise
        if (success) {
          result.push(p.provider.label)
        }
      } catch (err) {
        reportError(err, __filename)
      }
    }
    return result
  }
}

export function getAnalytics(): Analytics {
  return new Analytics()
}

/**
 * Shortcut to the new reporting function.
 */
export function track(
  context: Context,
  action: Action,
  description: string,
  data?: EventPropsSchema
): Promise<void> {
  return getAnalytics().tracks(context, action, description, data)
}
